import { useCallback, useEffect, useMemo, useState } from 'react'
import { differenceWith } from 'lodash'
import { IUser } from 'types/graphqlTypes'
import { useGlobalToast } from 'transfix-ui'
import { capitalize } from 'utils/capitalize'
import { useUserDetails } from 'utils/hooks'

type TUser = Pick<IUser, 'id' | 'firstName' | 'lastName'>
interface IHasUpdater {
  createdBy: TUser
  updater?: TUser
}

interface IUseNotesUpdateArgs<T> {
  /**
   * Function used in differenceWith to compare elements between new and previous data
   */
  comparator: (newValue: T, previousValue: T) => boolean
  loading: boolean
  newData: T[] | undefined
  previousData: T[] | undefined
  type: 'notes' | 'issues'
  /**
   * Whether or not changes in data should be determined.
   * ex. Used in Notes component when polling for updates is not required.
   */
  checkForUpdates?: boolean
  hasErrors: boolean
}

const getUpdaterName = (updater: IHasUpdater['updater']) => {
  const { firstName: updaterFirstName, lastName: updaterLastName } = updater || {}
  if (updaterFirstName && updaterLastName) return `${updaterFirstName} ${updaterLastName}`

  return 'A user'
}

/**
 * Determines if there has been a change/update between the new and previous data and updates what is displayed to the user accordingly.
 * If there was a change made by a different user, a toast is shown to the user.
 */
export const useNotesUpdate = <T extends IHasUpdater>({
  checkForUpdates = true,
  comparator,
  hasErrors = false,
  loading,
  newData,
  previousData,
  type,
}: IUseNotesUpdateArgs<T>) => {
  const { openToast } = useGlobalToast()
  const { userDetails } = useUserDetails()
  const { id } = userDetails || {}
  const currentUserId = `${id}`
  const [displayedData, setDisplayedData] = useState<IUseNotesUpdateArgs<T>['previousData']>(
    previousData
  )
  const [updateAvailable, setUpdateAvailable] = useState<boolean>(false)

  const difference = useMemo(() => {
    if (!checkForUpdates) return []
    return differenceWith(newData || [], previousData || [], comparator)
  }, [newData, previousData, comparator, checkForUpdates])

  useEffect(() => {
    if (!loading && checkForUpdates && difference.length) {
      const firstFetch = previousData === undefined
      const [latestChange] = difference || []
      const { createdBy, updater: updatedBy } = latestChange || {}
      const updater = updatedBy || createdBy
      const { id: updaterId } = updater || {}
      const showRefreshIndicator = !firstFetch && currentUserId !== updaterId
      if (!showRefreshIndicator) {
        setDisplayedData(newData)
        setUpdateAvailable(false)
      } else {
        setUpdateAvailable(true)
        openToast({
          toastMessage: `${getUpdaterName(updater)} has updated the ${capitalize(type)}`,
        })
      }
    } else if (!hasErrors) {
      setDisplayedData(newData)
    }
  }, [
    loading,
    newData,
    previousData,
    openToast,
    currentUserId,
    difference,
    type,
    checkForUpdates,
    hasErrors,
  ])

  const refreshHandler = useCallback(() => {
    setDisplayedData(newData)
    setUpdateAvailable(false)
  }, [newData])

  return {
    data: displayedData,
    refreshHandler,
    updateAvailable,
    setUpdateAvailable,
  }
}
